import { useState } from 'react';
import { Loader2, Search, Play, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function ConsolidatePersonalPanel() {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.consolidatePersonalPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.consolidatePersonalApply();
      setApplyResult(data);
      setPreview(null);
      toast.success(data.message);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const hasCredentialConcern = preview?.credential_concern?.action_needed;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Consolidate personal to fan_zhang_piotr_kacki</CardTitle>
        <p className="text-xs text-muted-foreground">
          Reassign properties and reservations tagged entity_id=&quot;personal&quot; to
          &quot;fan_zhang_piotr_kacki&quot; (same owner, Fan + Piotr). Idempotent.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={loading || applying} variant="outline">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          {preview && (preview.total_reservations > 0 || preview.property_count > 0) && (
            <Button onClick={runApply} disabled={applying} variant="default">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Apply
            </Button>
          )}
        </div>

        {/* Preview results */}
        {preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Properties</p>
                <p className="text-xl font-bold tabular-nums">{preview.property_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Reservations</p>
                <p className="text-xl font-bold tabular-nums">{preview.total_reservations}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Target</p>
                <p className="text-sm font-medium truncate" title={preview.target_entity_name}>
                  {preview.target_entity_name}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Jurisdiction</p>
                <p className="text-sm font-medium">{preview.target_jurisdiction || 'N/A'}</p>
              </div>
            </div>

            {/* Credential concern */}
            {hasCredentialConcern && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Credential concern
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {preview.credential_concern.message}
                </p>
              </div>
            )}

            {!hasCredentialConcern && preview.credential_concern && (
              <p className="text-xs text-muted-foreground">
                {preview.credential_concern.message}
              </p>
            )}

            {/* Property table */}
            {preview.properties.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium">Property ID</th>
                      <th className="text-left px-3 py-2 font-medium">Name</th>
                      <th className="text-left px-3 py-2 font-medium">Current entity_id</th>
                      <th className="text-right px-3 py-2 font-medium">Reservations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.properties.map((p) => (
                      <tr key={p.property_id} className="border-b">
                        <td className="px-3 py-2 font-mono text-xs">{p.property_id}</td>
                        <td className="px-3 py-2">{p.property_name}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-amber-600 border-amber-400 font-mono text-xs">
                            {p.current_entity_id}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{p.reservation_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.total_reservations === 0 && preview.property_count === 0 && (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">
                  No properties or reservations tagged &quot;personal&quot;. Already consolidated.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="rounded-lg border border-green-300 dark:border-green-800 p-3 bg-green-50 dark:bg-green-950/30 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Consolidation applied</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Properties reassigned</p>
                <p className="text-sm font-medium tabular-nums">{applyResult.properties_reassigned}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reservations reassigned</p>
                <p className="text-sm font-medium tabular-nums">{applyResult.reservations_reassigned}</p>
              </div>
            </div>
            {applyResult.orphaned_credential && (
              <div className="flex items-center gap-1.5 mt-1">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  A Pennylane credential still exists under &quot;personal&quot;. Fan should
                  re-connect under &quot;fan_zhang_piotr_kacki&quot; via Integrations.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
